import React, { useState, useRef, useCallback } from "react";
import {
  File,
  Image,
  Video,
  AudioWaveform,
  FileText,
  AlertCircle,
  CloudUpload,
} from "lucide-react";
import { ChatbotWidgetInitResponse } from "../../services/chatbotWidget.service";

interface MediaUploadProps {
  onUpload: (file: File) => Promise<void>;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
  maxSizeMB?: number;
  allowedTypes?: string[];
  config?: ChatbotWidgetInitResponse;
}

interface UploadState {
  isDragOver: boolean;
  isUploading: boolean;
  progress: number;
  error?: string;
  selectedFile?: File;
  previewUrl?: string;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  onUpload,
  onCancel,
  disabled = false,
  className = "",
  maxSizeMB = 100,
  allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "video/mp4",
    "video/quicktime",
    "video/x-msvideo",
    "audio/mpeg",
    "audio/wav",
    "audio/x-m4a",
  ],
  config,
}) => {
  const [state, setState] = useState<UploadState>({
    isDragOver: false,
    isUploading: false,
    progress: 0,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/"))
      return <Image size={24} className="text-blue-500" />;
    if (type.startsWith("video/"))
      return <Video size={24} className="text-purple-500" />;
    if (type.startsWith("audio/"))
      return <AudioWaveform size={24} className="text-green-500" />;
    if (
      type.includes("pdf") ||
      type.includes("document") ||
      type.includes("text")
    )
      return <FileText size={24} className="text-red-500" />;
    return <File size={24} className="text-gray-500" />;
  };

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        return `File size must be less than ${maxSizeMB}MB`;
      }

      // Check file type
      if (!allowedTypes.includes(file.type)) {
        return "This file type is not supported";
      }

      return null;
    },
    [maxSizeMB, allowedTypes],
  );

  const createPreviewUrl = (file: File): string | undefined => {
    if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
      return URL.createObjectURL(file);
    }
    return undefined;
  };

  const handleFileSelect = useCallback(
    (file: File) => {
      const error = validateFile(file);

      if (error) {
        setState((prev) => ({
          ...prev,
          error,
          selectedFile: undefined,
          previewUrl: undefined,
        }));
        return;
      }

      const previewUrl = createPreviewUrl(file);

      setState((prev) => ({
        ...prev,
        selectedFile: file,
        previewUrl,
        error: undefined,
      }));
    },
    [validateFile],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setState((prev) => ({ ...prev, isDragOver: true }));
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, isDragOver: false }));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setState((prev) => ({ ...prev, isDragOver: false }));

      if (disabled || !e.dataTransfer.files.length) return;

      const file = e.dataTransfer.files[0];
      handleFileSelect(file);
    },
    [disabled, handleFileSelect],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleUpload = async () => {
    if (!state.selectedFile || disabled) return;

    setState((prev) => ({
      ...prev,
      isUploading: true,
      progress: 0,
      error: undefined,
    }));

    try {
      await onUpload(state.selectedFile);

      // Clear state after successful upload
      setState({
        isDragOver: false,
        isUploading: false,
        progress: 0,
        selectedFile: undefined,
        previewUrl: undefined,
      });

      // Clear preview URL to prevent memory leaks
      if (state.previewUrl) {
        URL.revokeObjectURL(state.previewUrl);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isUploading: false,
        progress: 0,
        error: error instanceof Error ? error.message : "Upload failed",
      }));
    }
  };

  const handleCancel = () => {
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }

    setState({
      isDragOver: false,
      isUploading: false,
      progress: 0,
      selectedFile: undefined,
      previewUrl: undefined,
      error: undefined,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    onCancel?.();
  };

  return (
    <div className={`media-upload mt-0 ${className}`}>
      {!state.selectedFile ? (
        // File selection area
        <div
          className={`
            relative border-2 border-dashed rounded-md text-center transition-colors cursor-pointer p-4
            ${
              state.isDragOver
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-400"
            }
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedTypes.join(",")}
            onChange={handleFileInputChange}
            disabled={disabled}
            className="hidden"
          />

          <CloudUpload
            size={32}
            className={`mx-auto ${state.isDragOver ? "text-blue-500" : "text-gray-600"}`}
          />

          <p className=" font-medium text-gray-700 mb-1">
            {state.isDragOver ? "Drop your file here" : "Upload a file"}
          </p>

          <p className="text-xs text-gray-600 mb-2">
            Drag and drop or click to select a file
          </p>

          <p className="text-[10px] text-gray-400">
            Max size: {maxSizeMB}MB • Supported: Images, Videos, Documents,
            Audio
          </p>
        </div>
      ) : (
        // File preview and upload area
        <div className="space-y-4">
          {/* File preview */}
          <div className="relative bg-gray-50 px-3 py-3 overflow-hidden rounded-xl">
            <div className="flex items-center justify-center ">
              {state.previewUrl &&
              state.selectedFile.type.startsWith("image/") ? (
                <img
                  src={state.previewUrl}
                  alt="Preview"
                  className="w-full media-height rounded-md shadow-sm object-cover"
                />
              ) : state.previewUrl &&
                state.selectedFile.type.startsWith("video/") ? (
                <video
                  src={state.previewUrl}
                  className="w-full media-height rounded-md shadow-sm object-cover"
                  muted
                  autoPlay
                  loop
                />
              ) : (
                <div className="flex flex-col items-center gap-3 p-4 text-gray-400">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-gray-100">
                    {getFileIcon(state.selectedFile.type)}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700 ">
                      {state.selectedFile.name}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase font-bold mt-1 break-all">
                      {state.selectedFile.type.split('/')[1] || 'FILE'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Upload progress */}
            {state.isUploading && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Uploading...</span>
                  <span>{Math.round(state.progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${state.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {state.error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle size={16} />
              <span className="text-sm">{state.error}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 w-full">
            <button
              onClick={handleUpload}
              disabled={state.isUploading || disabled}
              data-testid="send-file-button"
              className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-100 transition-all duration-200 shadow-sm hover:shadow-md"
              style={{ backgroundColor: config?.secondaryColor || "#3b82f6" }}
            >
              {state.isUploading ? (
                <span className="flex items-center justify-center gap-2">
                  Sending...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Send File
                </span>
              )}
            </button>

            <button
              onClick={handleCancel}
              disabled={state.isUploading}
              className="w-max px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
